import {view, css} from '@sail/ui';
import * as React from 'react';
import {
  getColorBackgroundForCalculationsFromValue,
  getAccessibleColorFromBackgroundValue,
} from '@stripe-internal/embedded-theming';
import {useConnectJsOptionsContext} from '../../data-layer-client/ConnectJsOptionsContext/useConnectJsOptionsContext';
import {uuid} from '../uuid';
import {useThemeContext} from './ThemeContext/ThemeContext';

/**
 * Sets global styles for scrollbars. This is needed on every iframe that we render due to the classname inheritance nature of these global styles
 */
export const ScrollbarStyleOverrides: React.FC = ({children}) => {
  const {
    values: {appearance},
  } = useConnectJsOptionsContext();
  const {ignoreThemingValues} = useThemeContext();

  const scrollbarStylesForIframes = React.useMemo(() => {
    if (ignoreThemingValues) {
      return {stylesToApply: undefined, uniqueClassName: 'ignoreClassName'};
    }

    // Global styles are only additive, so we always need a unique classname
    const uniqueClassName = `scrollBarClass${uuid()}`;

    return {
      stylesToApply: getScrollbarStylesForClass(
        appearance.variables?.colorBackground,
        uniqueClassName,
      ),
      uniqueClassName,
    };
  }, [appearance.variables?.colorBackground, ignoreThemingValues]);

  return (
    <view.div
      className={scrollbarStylesForIframes.uniqueClassName}
      uses={[scrollbarStylesForIframes.stylesToApply]}
    >
      {children}
    </view.div>
  );
};
function getScrollbarStylesForClass(
  colorBackgroundInput: string | undefined,
  uniqueClassName: string | undefined,
) {
  const colorBackground =
    getColorBackgroundForCalculationsFromValue(colorBackgroundInput);

  // Derive the scrollbar and scrollbar background from the background color
  const colorScrollbarBackground = getAccessibleColorFromBackgroundValue(
    colorBackground,
    [900, 750],
    [0, 150],
  );
  // The color of the draggable bar within the scrollbar
  const colorScrollbarThumb = getAccessibleColorFromBackgroundValue(
    colorBackground,
    [900, 850],
    [0, 50],
  );

  if (!uniqueClassName) {
    return css.global({
      // This element is included at the root of every shadow DOM we have. Having it there ensures test color default value is always inherited
      // These css properties work for FireFox only
      '*': {
        scrollbarColor: `${colorScrollbarBackground} ${colorScrollbarThumb}`,
      },
      // These overrides work for Safari, Chrome, Edge
      '::-webkit-scrollbar': {
        background: colorScrollbarThumb, // Color of the draggable bar within the scrollbar,
      },
      '::-webkit-scrollbar-track': {
        background: colorScrollbarThumb, // Color of the draggable bar within the scrollbar,
      },
      '::-webkit-scrollbar-thumb': {
        // Colors
        backgroundColor: colorScrollbarBackground, // Color of the scrollbar background,
        borderColor: colorScrollbarThumb, // Color of the draggable bar within the scrollbar,
        // These values map to Chrome's default
        borderRadius: '20px',
        borderWidth: '3px',
        borderStyle: 'solid',
      },
    });
  }

  return css.global({
    // This element is included at the root of every shadow DOM we have. Having it there ensures test color default value is always inherited
    // These css properties work for FireFox only
    [`.${uniqueClassName} *`]: {
      scrollbarColor: `${colorScrollbarBackground} ${colorScrollbarThumb}`,
    },
    // These overrides work for Safari, Chrome, Edge
    [`.${uniqueClassName} ::-webkit-scrollbar`]: {
      background: colorScrollbarThumb, // Color of the draggable bar within the scrollbar,
    },
    [`.${uniqueClassName} ::-webkit-scrollbar-track`]: {
      background: colorScrollbarThumb, // Color of the draggable bar within the scrollbar,
    },
    [`.${uniqueClassName} ::-webkit-scrollbar-thumb`]: {
      // Colors
      backgroundColor: colorScrollbarBackground,
      borderColor: colorScrollbarThumb,

      // These values map to Chrome's default
      borderRadius: '20px',
      borderWidth: '3px',
      borderStyle: 'solid',
    },
  });
}
